import * as XLSX from 'xlsx';
import Url from '../global/Url';
import { getCsrf } from './auth';

/**
 * Carga flexible a la bitácora de Fusión (leads_fusion.bitacora_fusion).
 * El Excel de Fusión trae muchas columnas; aquí SOLO se toman las que usa el
 * formulario, detectándolas por nombre (tolera acentos, mayúsculas y variantes).
 * Las columnas que falten quedan vacías; solo el Teléfono es obligatorio.
 */

// Campo destino (bitácora) + alias de encabezado que se aceptan (ya normalizados).
export const CAMPOS = [
    { key: 'telefono',              label: 'Teléfono',        req: true,  alias: ['telefono', 'celular', 'numero', 'movil', 'phone', 'nro'] },
    { key: 'id_fusion',             label: 'Id Fusión',       alias: ['id', 'id fusion', 'idfusion', 'id fusión'] },
    { key: 'ci_fusion',             label: 'CI Fusión',       alias: ['ci fusion', 'ci fusión', 'cedula', 'ci', 'identificacion'] },
    { key: 'nombre_fusion',         label: 'Nombre Fusión',   alias: ['nombre fusion', 'nombre fusión', 'nombre', 'nombres', 'cliente'] },
    { key: 'canal',                 label: 'Canal',           alias: ['canal'] },
    { key: 'campania_fusion',       label: 'Campaña',         alias: ['campana', 'campania', 'campaña', 'campaign'] },
    { key: 'producto',              label: 'Producto',        alias: ['producto', 'product'] },
    { key: 'fecha_creacion_fusion', label: 'Fecha creación',  alias: ['fecha creacion', 'fecha creación', 'fecha'] },
    { key: 'hora_creacion_fusion',  label: 'Hora creación',   alias: ['hora creacion', 'hora creación', 'hora'] },
];

const FILAS_POR_LOTE = 2000;

// Normaliza un encabezado: minúsculas, sin acentos, solo letras/números/espacios.
const norm = (s) => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

// Homologa el teléfono al formato 09XXXXXXXX (Fusión suele traer 9 díg: 9XXXXXXXX).
const homolog = (t) => { t = String(t).replace(/\D/g, ''); return (t.length === 9 && t[0] === '9') ? '0' + t : t; };

/** Lee Excel/CSV -> { headers (crudos), filas (array de arrays alineado a headers) }. */
export function leerArchivo(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const matriz = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
                if (matriz.length === 0) { reject(new Error('El archivo está vacío')); return; }
                const headers = matriz[0].map((h) => String(h).trim());
                resolve({ headers, filas: matriz.slice(1) });
            } catch (e) {
                reject(new Error('No se pudo leer el archivo: ' + e.message));
            }
        };
        reader.onerror = () => reject(new Error('Error al leer el archivo'));
        reader.readAsBinaryString(file);
    });
}

/** Detecta qué columna del archivo corresponde a cada campo destino. */
export function detectarMapeo(headers) {
    const nh = headers.map((h, i) => ({ i, n: norm(h), raw: h }));
    const usados = new Set();
    const mapeo = {};
    for (const c of CAMPOS) {
        let found = null;
        // 1) match exacto por alias
        for (const al of c.alias) {
            const m = nh.find((h) => !usados.has(h.i) && h.n === al);
            if (m) { found = m; break; }
        }
        // 2) fallback: el encabezado contiene el alias (o viceversa)
        if (!found) {
            for (const al of c.alias) {
                const m = nh.find((h) => !usados.has(h.i) && (h.n === al || h.n.split(' ').includes(al)));
                if (m) { found = m; break; }
            }
        }
        if (found) usados.add(found.i);
        mapeo[c.key] = found ? { index: found.i, header: found.raw } : null;
    }
    return mapeo;
}

/** Construye las filas destino, homologa teléfono y deduplica dentro del archivo. */
export function construirFilas(filas, mapeo) {
    const out = [];
    const vistos = new Set();
    let dupInternos = 0, invalidos = 0;
    for (const fila of filas) {
        if (!fila || fila.every((c) => String(c).trim() === '')) continue;
        const get = (key) => (mapeo[key] ? String(fila[mapeo[key].index] ?? '').trim() : '');
        const telRaw = get('telefono');
        const tel = homolog(telRaw);
        if (!/^09[0-9]{8}$/.test(tel)) { invalidos++; continue; }
        if (vistos.has(tel)) { dupInternos++; continue; }
        vistos.add(tel);
        out.push({
            telefono: tel,
            telefono_fusion: String(telRaw).replace(/\D/g, ''),
            id_fusion: get('id_fusion'),
            ci_fusion: get('ci_fusion'),
            nombre_fusion: get('nombre_fusion'),
            canal: get('canal'),
            campania_fusion: get('campania_fusion'),
            producto: get('producto'),
            fecha_creacion_fusion: get('fecha_creacion_fusion'),
            hora_creacion_fusion: get('hora_creacion_fusion'),
        });
    }
    return { filas: out, dupInternos, invalidos };
}

/** Sube en lotes a /fusion/cargarBitacora. */
export async function subir(filas, onProgreso) {
    const total = { insertadas: 0, existentes: 0, invalidas: 0, procesadas: 0, totalFilas: filas.length };
    for (let i = 0; i < filas.length; i += FILAS_POR_LOTE) {
        const lote = filas.slice(i, i + FILAS_POR_LOTE);
        const resp = await fetch(Url.Api + 'fusion/cargarBitacora', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF': getCsrf() || '' },
            body: JSON.stringify({ rows: lote }),
        });
        const data = await resp.json();
        if (!resp.ok || data.header !== 'OK') throw new Error(data.message || 'Error del servidor en un lote');
        total.insertadas += data.insertadas;
        total.existentes += data.existentes;
        total.invalidas += (data.invalidas || 0);
        total.procesadas += lote.length;
        if (onProgreso) onProgreso({ ...total });
    }
    return total;
}
